import { TypedEventTarget } from 'typescript-event-target';
import { ChangeEvent, LogEvent } from "./events.js";
import { levels } from "./levels.js";
/**
 * A FIFO logger that stores the most recent events in an array.
 */
export class FifoLogger extends TypedEventTarget {
    lastID;
    initialOptions;
    events;
    uuids;
    levelAsNumber;
    limit;
    bindings;
    level;
    constructor(options = {}) {
        super();
        this.lastID = { id: 0 };
        this.initialOptions = options;
        this.uuids = [crypto.randomUUID()];
        this.events = [];
        this.level = options.level || 'info';
        this.levelAsNumber = levels.values[this.level];
        this.limit = options.limit ?? 1000;
        this.bindings = options.bindings ?? {};
    }
    setLevel(level) {
        this.level = level;
        this.levelAsNumber = levels.values[level];
    }
    setLimit(limit) {
        this.limit = limit;
        this.checkSize();
    }
    checkSize() {
        if (this.events.length > this.limit) {
            this.events.splice(0, this.events.length - this.limit);
        }
    }
    /**
     * Remove events from the current logger and its children.
     */
    clear() {
        for (let i = this.events.length - 1; i >= 0; i--) {
            if (this.events[i].uuids.includes(this.uuids[0])) {
                this.events.splice(i, 1);
            }
        }
        this.dispatchTypedEvent('change', new ChangeEvent({
            logs: this.events,
            info: { depth: this.uuids.length },
        }));
    }
    /**
     * Get a filtered list of all log events.
     * @param options - Options.
     * @returns Array of filtered logs.
     */
    getLogs(options = {}) {
        const { level, minLevel, includeChildren } = options;
        let logs = this.events.slice();
        if (includeChildren) {
            logs = logs.filter((log) => log.uuids.includes(this.uuids[0]));
        }
        else {
            logs = logs.filter((log) => log.uuids[0] === this.uuids[0]);
        }
        if (level) {
            const levelNumber = levels.values[level];
            if (Number.isNaN(levelNumber)) {
                throw new Error('Invalid level');
            }
            logs = logs.filter((log) => log.level === levelNumber);
        }
        if (minLevel) {
            const levelNumber = levels.values[minLevel];
            if (Number.isNaN(levelNumber)) {
                throw new Error('Invalid level');
            }
            logs = logs.filter((log) => log.level >= levelNumber);
        }
        return logs;
    }
    /**
     * Create a child logger with additional bindings.
     * @param bindings - an object of key-value pairs to include in log lines as properties.
     * @returns The new child logger.
     */
    child(bindings) {
        const newFifoLogger = new FifoLogger(this.initialOptions);
        newFifoLogger.events = this.events;
        newFifoLogger.uuids = [crypto.randomUUID(), ...this.uuids];
        newFifoLogger.lastID = this.lastID;
        newFifoLogger.bindings = { ...this.bindings, ...bindings };
        newFifoLogger.addEventListener('change', (event) => {
            this.dispatchTypedEvent('change', new ChangeEvent(event.detail));
        });
        return newFifoLogger;
    }
    trace(value, message) {
        this.#addEvent(levels.values.trace, value, message);
    }
    debug(value, message) {
        this.#addEvent(levels.values.debug, value, message);
    }
    info(value, message) {
        this.#addEvent(levels.values.info, value, message);
    }
    warn(value, message) {
        this.#addEvent(levels.values.warn, value, message);
    }
    error(value, message) {
        this.#addEvent(levels.values.error, value, message);
    }
    fatal(value, message) {
        this.#addEvent(levels.values.fatal, value, message);
    }
    #addEvent(level, value, message) {
        if (level < this.levelAsNumber)
            return;
        const event = {
            id: ++this.lastID.id,
            level,
            levelLabel: levels.labels[level],
            time: Date.now(),
            uuids: this.uuids,
            message: '',
        };
        if (value instanceof Error) {
            event.message = value.toString();
            event.error = value;
            event.meta = { ...this.bindings };
        }
        else if (message !== undefined && typeof value === 'object') {
            event.message = message;
            event.meta = { ...this.bindings, ...value };
        }
        else if (message === undefined && typeof value === 'string') {
            event.message = value;
            event.meta = { ...this.bindings };
        }
        else {
            throw new Error('Invalid arguments');
        }
        this.events.push(event);
        this.checkSize();
        this.dispatchTypedEvent('log', new LogEvent({
            log: event,
            logs: this.events,
            info: { depth: this.uuids.length },
        }));
        this.dispatchTypedEvent('change', new ChangeEvent({
            logs: this.events,
            info: { depth: this.uuids.length },
        }));
    }
}
//# sourceMappingURL=FifoLogger.js.map